import { useCallback, useEffect, useState } from 'react';
import usePfqAxios from './usePfqAxios';
import Constants from '../Constants';
import { INTEGRATION_PARTNER } from '../utils/enum';
import { casePeerLogin } from '../utils/casepeerUtil';
import { errorToast } from '../utils/toast';

export const DEFAULT_PARTNER_PAGE_SIZE = 5;

/**
 * Builds the partner cases query. Exported for the test, because the cursor and the case id are the
 * two parameters whose absence changes what comes back.
 */
export const buildPartnerCasesUrl = ({ partner, pageSize, cursor, search, caseId }) => {
  const params = new URLSearchParams({ pageSize: String(pageSize) });

  if (cursor) params.set('cursor', cursor);
  if (search) params.set('search', search);
  // the backend matches on it and may import directly rather than returning a list to choose from
  if (caseId) params.set('caseId', caseId);

  return `${Constants.ApiUrl.partner.getCases}/${partner}?${params.toString()}`;
};

const EMPTY_PAGE = { cases: [], nextCursor: null, prevCursor: null, totalCount: 0 };

// CASEpeer is the one partner that can answer "no" because the user has not authorized us yet, and
// the fix is a redirect rather than an error message.
const getIsCasePeerAuthRedirect = ({ partner, data }) =>
  partner === INTEGRATION_PARTNER.CASEPEER && Boolean(data?.requiresOAuth);

/**
 * Lists one partner's cases with cursor pagination and search.
 * @param {{ partner, caseId, pageSize }} params - partner is null when nothing is selected
 */
const usePartnerCaseList = ({ partner, caseId, pageSize = DEFAULT_PARTNER_PAGE_SIZE }) => {
  const { getData, isLoading } = usePfqAxios();
  const [page, setPage] = useState(EMPTY_PAGE);
  const [cursor, setCursor] = useState(null);
  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [directImport, setDirectImport] = useState(null);

  const reset = useCallback(() => {
    setPage(EMPTY_PAGE);
    setCursor(null);
    setSearch('');
    setPageNumber(1);
    setDirectImport(null);
  }, []);

  // a new search is a new result set, so it cannot keep the cursor it was paging with
  const searchFor = useCallback((term) => {
    setSearch(term);
    setCursor(null);
    setPageNumber(1);
  }, []);

  useEffect(() => {
    if (!partner) return;

    let isStale = false;

    getData(buildPartnerCasesUrl({ partner, pageSize, cursor, search, caseId }))
      .then(({ success, data, message, meta }) => {
        if (isStale) return;

        if (!success) {
          if (getIsCasePeerAuthRedirect({ partner, data })) {
            casePeerLogin({ clientId: data?.clientId });

            return;
          }

          errorToast(message || 'There is some error. Please try again.');

          return;
        }

        setPage({
          cases: data ?? [],
          nextCursor: meta?.pagination?.next ?? null,
          prevCursor: meta?.pagination?.prev ?? null,
          totalCount: meta?.pagination?.total_count ?? 0
        });

        if (meta?.directImport) setDirectImport(meta.directImport);
      })
      .catch((error) => {
        if (isStale) return;

        console.error('Error fetching cases:', error);
        errorToast('Failed to fetch cases. Please try again.');
      });

    // a partner switched mid-flight must not have its list land over the new one
    return () => { isStale = true; };
  }, [partner, caseId, pageSize, cursor, search, getData]);

  const goToNextPage = useCallback(() => {
    setPage((previous) => {
      if (previous.nextCursor) {
        setCursor(previous.nextCursor);
        setPageNumber((current) => current + 1);
      }

      return previous;
    });
  }, []);

  const goToPreviousPage = useCallback(() => {
    setPage((previous) => {
      if (previous.prevCursor) {
        setCursor(previous.prevCursor);
        setPageNumber((current) => current - 1);
      }

      return previous;
    });
  }, []);

  return {
    cases: page.cases,
    totalCount: page.totalCount,
    totalPages: pageSize > 0 ? Math.ceil(page.totalCount / pageSize) : 0,
    pageNumber,
    hasNextPage: Boolean(page.nextCursor),
    hasPreviousPage: Boolean(page.prevCursor),
    isLoading,
    search,
    directImport,
    searchFor,
    goToNextPage,
    goToPreviousPage,
    reset
  };
};

export default usePartnerCaseList;
